import { useEffect, useRef } from 'react';

const TAX_LINE_FIELDS = [
  'id', 'accountId', 'creditLessDebit', 'memo', 'assetId', 'saleAssetId',
];

export const findTaxIndex = (lines, taxAccountId) =>
  lines.findIndex(line =>
    (line.account?.id || line.accountId) === taxAccountId
  );

// Adds/removes the SALES TAX PD line in the invoice form's line list as the
// user toggles applySalesTax. The initial value is the current estimate; the
// user can edit it freely after that — subsequent estimate updates do NOT
// overwrite it. The backend trusts whatever value is in form state on save
// and rebalances the bank line via balanceAccountId.
export const useTaxLineMirror = ({
  applySalesTax,
  estimatedTax,
  estimateReady,
  taxAccountId,
  nestedResources,
  setNestedResources,
  emptyLine,
  register,
  unregister,
  getValues,
  setValue,
}) => {
  const prevApplyOn = useRef(!!applySalesTax);

  useEffect(() => {
    const wasOn = prevApplyOn.current;
    const isOn = !!applySalesTax;
    const taxIdx = findTaxIndex(nestedResources, taxAccountId);

    if (!isOn && wasOn && taxIdx >= 0) {
      // Shift values down so visible row order stays aligned with form-state
      // keys, then drop the now-orphaned last index.
      const lastIdx = nestedResources.length - 1;
      for (let i = taxIdx; i < lastIdx; i++) {
        TAX_LINE_FIELDS.forEach(name => {
          const next = getValues(`txnLinesAttributes.${i + 1}.${name}`);
          setValue(`txnLinesAttributes.${i}.${name}`, next);
        });
      }
      unregister(`txnLinesAttributes.${lastIdx}`);
      setNestedResources(nestedResources.filter((_, i) => i !== taxIdx));
      prevApplyOn.current = isOn;
      return;
    }

    if (isOn && taxIdx < 0 && estimateReady) {
      const idx = nestedResources.length;
      const prefix = `txnLinesAttributes.${idx}`;
      register(`${prefix}.accountId`, { value: taxAccountId });
      register(`${prefix}.creditLessDebit`, { value: estimatedTax });
      register(`${prefix}.memo`, { value: '' });
      register(`${prefix}.assetId`, { value: null });
      register(`${prefix}.saleAssetId`, { value: null });
      setNestedResources([...nestedResources, {
        ...emptyLine,
        accountId: taxAccountId,
        creditLessDebit: estimatedTax,
        account: { id: taxAccountId, name: 'SALES TAX PD', special: 'sales_tax' },
      }]);
      prevApplyOn.current = isOn;
      return;
    }

    prevApplyOn.current = isOn;
  }, [applySalesTax, estimateReady]);
};
